import React from 'react';
import { VoiceLayout } from './VoiceLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { usePageTitle } from '@/lib/usePageTitle';
import { formatNumber, formatPct } from '@/lib/format';
import { cn } from '@/lib/utils';

const ANALYTICS = [
  { l: 'Calls attempted',          v: 1480, t: 12 },
  { l: 'Calls connected',          v: 970,  t: 18 },
  { l: 'Right-party contacts',     v: 689,  t: 9 },
  { l: 'Verified debtors',         v: 842,  t: 22 },
  { l: 'PTP rate',                 v: 0.41, format: 'pct', t: 6 },
  { l: 'Kept promise rate',        v: 0.78, format: 'pct', t: 4 },
  { l: 'Avg promise amount',       v: 4820, format: 'sar', t: 8 },
  { l: 'Plan conversion',          v: 0.18, format: 'pct', t: 12 },
  { l: 'Hardship detection',       v: 0.04, format: 'pct', t: 1 },
  { l: 'Dispute detection',        v: 0.05, format: 'pct', t: 2 },
  { l: 'Human escalation',         v: 0.09, format: 'pct', t: -8 },
  { l: 'Cost per contact (SAR)',   v: 1.92, format: 'num', t: -22 },
  { l: 'Agent-hours saved',        v: 312,  t: 14 },
  { l: 'Recovery influenced',      v: 0.62, format: 'pct', t: 7 },
  { l: 'Compliance pass rate',     v: 0.984, format: 'pct', t: 1 },
  { l: 'Customer sentiment',       v: 0.71, format: 'pct', t: 3 },
];

const COMPARISONS = [
  { l: 'AI Agent', vs: 'Human Agent', a: 64.2, b: 42.8 },
  { l: 'Sara (Empathetic)', vs: 'Fahad (Firm)', a: 64.2, b: 58.7 },
  { l: 'Retail — Early', vs: 'Auto — 30 DPD', a: 64.2, b: 58.7 },
  { l: 'Saudi dialect script', vs: 'MSA formal script', a: 67.4, b: 53.2 },
  { l: 'Voice call', vs: 'WhatsApp follow-up', a: 41.0, b: 21.8 },
];

const FUNNEL = [
  { l: 'Dialled',          v: 1480 },
  { l: 'Connected',        v: 970  },
  { l: 'Verified',         v: 842  },
  { l: 'Balance presented',v: 786  },
  { l: 'Option accepted',  v: 542  },
  { l: 'Promise captured', v: 342  },
  { l: 'Payment confirmed',v: 64   },
];

export default function VoiceAnalytics() {
  usePageTitle('AI Voice Analytics');
  return (
    <VoiceLayout
      title="AI voice analytics"
      subtitle="Performance, recovery influence and comparison cohorts for every AI voice agent."
      actions={(<Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1.5" aria-hidden="true" />Export report</Button>)}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2.5">
        {ANALYTICS.map((m) => (
          <div key={m.l} className="surface-card p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">{m.l}</p>
            <p className="font-mono-num text-lg font-semibold mt-0.5">{m.format === 'pct' ? formatPct(m.v) : m.format === 'sar' ? m.v.toLocaleString() : formatNumber(m.v)}</p>
            <span className={cn('text-[10px]', m.t >= 0 ? 'kpi-trend-up' : 'kpi-trend-down')}>{m.t >= 0 ? '↑' : '↓'} {Math.abs(m.t)}%</span>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="surface-card p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Recovery funnel — last 30 days</p>
          <div className="space-y-1.5" role="img" aria-label="Recovery funnel">
            {FUNNEL.map((r, i) => {
              const pct = r.v / FUNNEL[0].v;
              return (
                <div key={r.l} className="flex items-center gap-2">
                  <span className="text-[11px] w-36 truncate">{r.l}</span>
                  <div className="flex-1 h-4 bg-muted rounded">
                    <div className="h-full bg-primary/80 rounded" style={{ width: `${pct * 100}%` }} />
                  </div>
                  <span className="font-mono-num text-[11px] w-12 text-right">{formatNumber(r.v)}</span>
                  <span className="font-mono-num text-[10px] text-muted-foreground w-10 text-right">{Math.round(pct * 100)}%</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="surface-card p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">A/B comparisons (success rate %)</p>
          <div className="space-y-3">
            {COMPARISONS.map((c) => (
              <div key={c.l}>
                <div className="flex justify-between text-[11px]">
                  <span>{c.l} <Badge variant="outline" className="text-[10px] ml-1">{c.a}%</Badge></span>
                  <span className="text-muted-foreground">vs {c.vs} <Badge variant="outline" className="text-[10px] ml-1">{c.b}%</Badge></span>
                </div>
                <div className="h-3 mt-1 rounded-full bg-muted overflow-hidden flex">
                  <div className="bg-primary" style={{ width: `${(c.a / (c.a + c.b)) * 100}%` }} />
                  <div className="bg-onyx/30" style={{ width: `${(c.b / (c.a + c.b)) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </VoiceLayout>
  );
}
